import { getUserUidDisplayLabel } from "../TableHelper.tsx"
import { resolveItemCreatorUid, resolveItemOwnerUid } from "../resolveItemOwnerUid"

export function buildNameToFieldMeta(metaRoot: unknown): Record<string, any> {
  const fields =
    metaRoot &&
      typeof metaRoot === "object" &&
      !Array.isArray(metaRoot) &&
      Array.isArray((metaRoot as { fields?: unknown }).fields)
      ? ((metaRoot as { fields: any[] }).fields as any[])
      : []
  const out: Record<string, any> = {}
  fields.forEach((f: any, i: number) => {
    const name = f?.name || `field_${i}`
    out[name] = f
  })
  out._creator = {}
  out._itemOwner = {}
  out._created = {}
  out._updated = {}
  return out
}

function normalizeTimestampKey(v: unknown): string {
  if (v == null) return "__empty__"
  if (typeof v === "object" && v !== null && "seconds" in (v as Record<string, unknown>)) {
    const s = (v as { seconds?: unknown }).seconds
    return typeof s === "number" && Number.isFinite(s) ? `ts:${s}` : "__empty__"
  }
  if (v instanceof Date) {
    const t = v.getTime()
    return Number.isFinite(t) ? `ts:${Math.floor(t / 1000)}` : "__empty__"
  }
  const d = new Date(v as string | number)
  if (!Number.isNaN(d.getTime())) return `ts:${Math.floor(d.getTime() / 1000)}`
  return `s:${String(v).trim()}` || "__empty__"
}

export function valueToDedupeKey(raw: unknown): string {
  if (raw == null) return "__empty__"
  if (typeof raw === "boolean") return raw ? "bool:1" : "bool:0"
  if (typeof raw === "number" && Number.isFinite(raw)) return `num:${raw}`
  if (typeof raw === "string") {
    const t = raw.trim()
    return t === "" ? "__empty__" : `s:${t}`
  }
  if (Array.isArray(raw)) {
    const parts = raw.map((x) => valueToDedupeKey(x)).sort()
    return `arr:${parts.join("|")}`
  }
  if (typeof raw === "object") {
    const o = raw as Record<string, unknown>
    if (typeof o.uid === "string" && o.uid.trim()) return `uid:${o.uid.trim()}`
    try {
      return `obj:${JSON.stringify(raw)}`
    } catch {
      return `obj:${String(raw)}`
    }
  }
  return `x:${String(raw)}`
}

export function getComparableKey(
  row: Record<string, unknown>,
  fieldName: string,
  nameToFieldMeta: Record<string, any>
): string {
  const fieldMeta = nameToFieldMeta[fieldName]
  if (fieldName === "_creator") {
    const u = resolveItemCreatorUid(row)
    return u == null || String(u).trim() === "" ? "__empty__" : String(u).trim()
  }
  if (fieldName === "_itemOwner") {
    const u = resolveItemOwnerUid(row)
    return u == null || String(u).trim() === "" ? "__empty__" : String(u).trim()
  }
  if (fieldName === "_created") {
    const v =
      (row as { createdAt?: unknown; _created?: unknown }).createdAt ??
      (row as { _created?: unknown })._created
    return normalizeTimestampKey(v)
  }
  if (fieldName === "_updated") {
    const v =
      (row as { updatedAt?: unknown; _updated?: unknown }).updatedAt ??
      (row as { _updated?: unknown })._updated
    return normalizeTimestampKey(v)
  }

  let raw: unknown
  if (fieldMeta?.type === "systemrelation" && fieldMeta?.sourceField) {
    const sf = fieldMeta.sourceField as string
    raw =
      typeof sf === "string" && sf.includes(".")
        ? sf.split(".").reduce((acc: unknown, k: string) => (acc as any)?.[k], row)
        : (row as Record<string, unknown>)[sf]
    if (raw === undefined) raw = (row as Record<string, unknown>)[fieldName]
  } else {
    raw = (row as Record<string, unknown>)[fieldName]
  }
  return valueToDedupeKey(raw)
}

export function formatKeyForDisplay(fieldName: string, key: string): string {
  if (key === "__empty__") return "(empty)"
  if (fieldName === "_creator" || fieldName === "_itemOwner") {
    return getUserUidDisplayLabel(key) || key
  }
  if (key.startsWith("ts:")) {
    const sec = parseInt(key.slice(3), 10)
    if (Number.isFinite(sec)) {
      const d = new Date(sec * 1000)
      if (!Number.isNaN(d.getTime())) return d.toLocaleString()
    }
    return key.slice(3)
  }
  if (key.startsWith("num:")) return key.slice(4)
  if (key.startsWith("s:")) return key.slice(2)
  if (key.startsWith("uid:")) return key.slice(4)
  if (key.startsWith("bool:")) return key.endsWith("1") ? "true" : "false"
  if (key.startsWith("arr:")) return key.slice(4).replace(/\|/g, ", ")
  return key
}
